import { useState } from "react";
import { useTrackActivity } from "@/hooks/useActivityTracker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Download, CheckCircle, AlertTriangle, XCircle, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";

interface TablePolicy {
  table: string;
  rlsEnabled: boolean;
  policies: {
    name: string;
    command: string;
    permissive: boolean;
  }[];
}

interface SecurityConfig {
  authAutoConfirm: boolean;
  authAnonymousEnabled: boolean;
  storageBuckets: { name: string; isPublic: boolean }[];
}

const SECURITY_DATA: { tables: TablePolicy[]; config: SecurityConfig } = {
  tables: [
    {
      table: "alerts",
      rlsEnabled: true,
      policies: [
        { name: "Users can delete own alerts", command: "DELETE", permissive: false },
        { name: "Users can insert own alerts", command: "INSERT", permissive: false },
        { name: "Users can update own alerts", command: "UPDATE", permissive: false },
        { name: "Users can view own alerts or admins all", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "client_users",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete client_users", command: "DELETE", permissive: false },
        { name: "Admins can insert client_users", command: "INSERT", permissive: false },
        { name: "Admins can update client_users", command: "UPDATE", permissive: false },
        { name: "Super users can delete their users", command: "DELETE", permissive: false },
        { name: "Super users can insert their users", command: "INSERT", permissive: false },
        { name: "Super users can update their users", command: "UPDATE", permissive: false },
        { name: "Users can view authorized client_users", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "clients",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete clients", command: "DELETE", permissive: false },
        { name: "Admins can insert clients", command: "INSERT", permissive: false },
        { name: "Admins can update clients", command: "UPDATE", permissive: false },
        { name: "Authenticated users can view authorized clients", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "cpv_codes",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete CPV codes", command: "DELETE", permissive: false },
        { name: "Admins can insert CPV codes", command: "INSERT", permissive: false },
        { name: "Admins can update CPV codes", command: "UPDATE", permissive: false },
        { name: "Anyone can view CPV codes", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "cpv_translations",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete CPV translations", command: "DELETE", permissive: false },
        { name: "Admins can insert CPV translations", command: "INSERT", permissive: false },
        { name: "Admins can update CPV translations", command: "UPDATE", permissive: false },
        { name: "Anyone can view CPV translations", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "notifications",
      rlsEnabled: true,
      policies: [
        { name: "Admins can insert notifications", command: "INSERT", permissive: false },
        { name: "Users can delete own notifications", command: "DELETE", permissive: false },
        { name: "Users can insert own notifications", command: "INSERT", permissive: false },
        { name: "Users can update own notifications", command: "UPDATE", permissive: false },
        { name: "Users can view own notifications or admins all", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "profiles",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete profiles", command: "DELETE", permissive: false },
        { name: "Admins can insert profiles", command: "INSERT", permissive: false },
        { name: "Super users can view client profiles", command: "SELECT", permissive: false },
        { name: "Users can update own profile", command: "UPDATE", permissive: false },
        { name: "Users can view own profile or admins all", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "releases",
      rlsEnabled: true,
      policies: [
        { name: "Admins can manage releases", command: "ALL", permissive: false },
        { name: "Anyone can view published releases", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "support_tickets",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete tickets", command: "DELETE", permissive: false },
        { name: "Admins can update tickets", command: "UPDATE", permissive: false },
        { name: "Authenticated users can create tickets", command: "INSERT", permissive: false },
        { name: "Users can view own tickets", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "ted_results",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete results", command: "DELETE", permissive: false },
        { name: "Admins can insert results", command: "INSERT", permissive: false },
        { name: "Admins can update all results", command: "UPDATE", permissive: false },
        { name: "Users can update their client results", command: "UPDATE", permissive: false },
        { name: "Users can view authorized results", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "ted_searches",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete searches", command: "DELETE", permissive: false },
        { name: "Admins can insert searches", command: "INSERT", permissive: false },
        { name: "Admins can update searches", command: "UPDATE", permissive: false },
        { name: "Super users can delete their searches", command: "DELETE", permissive: false },
        { name: "Super users can insert their searches", command: "INSERT", permissive: false },
        { name: "Super users can update their searches", command: "UPDATE", permissive: false },
        { name: "Users can delete their searches", command: "DELETE", permissive: false },
        { name: "Users can insert their searches", command: "INSERT", permissive: false },
        { name: "Users can update their searches", command: "UPDATE", permissive: false },
        { name: "Users can view authorized searches", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "ticket_replies",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete replies", command: "DELETE", permissive: false },
        { name: "Admins can insert replies", command: "INSERT", permissive: false },
        { name: "Admins can view all replies", command: "SELECT", permissive: false },
        { name: "Users can view replies to their tickets", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "user_daily_searches",
      rlsEnabled: true,
      policies: [
        { name: "Users can delete their own search counts", command: "DELETE", permissive: false },
        { name: "Users can insert their own search counts", command: "INSERT", permissive: false },
        { name: "Users can update their own search counts", command: "UPDATE", permissive: false },
        { name: "Users can view their own search counts", command: "SELECT", permissive: false },
      ],
    },
    {
      table: "user_roles",
      rlsEnabled: true,
      policies: [
        { name: "Admins can delete roles", command: "DELETE", permissive: false },
        { name: "Admins can insert roles", command: "INSERT", permissive: false },
        { name: "Admins can update roles", command: "UPDATE", permissive: false },
        { name: "Users can view authorized roles", command: "SELECT", permissive: false },
      ],
    },
  ],
  config: {
    authAutoConfirm: true,
    authAnonymousEnabled: false,
    storageBuckets: [{ name: "ticket-attachments", isPublic: false }],
  },
};

export function SecurityReportGenerator() {
  const { t } = useTranslation();
  const trackActivity = useTrackActivity();
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async () => {
    setIsGenerating(true);

    try {
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;
      const autoTableModule = await import('jspdf-autotable');
      const autoTable = autoTableModule.default;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Title
      doc.setFontSize(24);
      doc.setTextColor(30, 64, 175);
      doc.text("TED Monitor - Security Report", pageWidth / 2, yPos, { align: "center" });
      yPos += 15;

      // Date
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: "center" });
      yPos += 20;

      // Executive Summary
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("Executive Summary", 14, yPos);
      yPos += 10;

      const totalTables = SECURITY_DATA.tables.length;
      const tablesWithRLS = SECURITY_DATA.tables.filter((t) => t.rlsEnabled).length;
      const totalPolicies = SECURITY_DATA.tables.reduce((acc, t) => acc + t.policies.length, 0);

      doc.setFontSize(11);
      doc.text(`• Total Tables: ${totalTables}`, 20, yPos);
      yPos += 7;
      doc.text(`• Tables with RLS Enabled: ${tablesWithRLS} (${Math.round((tablesWithRLS / totalTables) * 100)}%)`, 20, yPos);
      yPos += 7;
      doc.text(`• Total RLS Policies: ${totalPolicies}`, 20, yPos);
      yPos += 7;
      doc.text(`• Anonymous Sign-ups: ${SECURITY_DATA.config.authAnonymousEnabled ? "Enabled" : "Disabled"}`, 20, yPos);
      yPos += 7;
      doc.text(`• Email Auto-Confirm: ${SECURITY_DATA.config.authAutoConfirm ? "Enabled" : "Disabled"}`, 20, yPos);
      yPos += 15;

      // Security Status
      const securityStatus = tablesWithRLS === totalTables ? "SECURE" : "NEEDS ATTENTION";
      doc.setFontSize(14);
      doc.setTextColor(tablesWithRLS === totalTables ? 22 : 220, tablesWithRLS === totalTables ? 163 : 38, tablesWithRLS === totalTables ? 74 : 38);
      doc.text(`Overall Security Status: ${securityStatus}`, 14, yPos);
      yPos += 15;

      // RLS Policies Table
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.text("RLS Policies by Table", 14, yPos);
      yPos += 5;

      const tableData = SECURITY_DATA.tables.map((table) => [
        table.table,
        table.rlsEnabled ? "✓ Enabled" : "✗ Disabled",
        table.policies.length.toString(),
        table.policies.map((p) => p.command).filter((v, i, a) => a.indexOf(v) === i).join(", "),
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [["Table", "RLS Status", "Policies", "Operations"]],
        body: tableData,
        theme: "striped",
        headStyles: { fillColor: [30, 64, 175] },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { fontStyle: "bold" },
          1: { halign: "center" },
          2: { halign: "center" },
        },
      });

      // New page for detailed policies
      doc.addPage();
      yPos = 20;

      doc.setFontSize(16);
      doc.text("Detailed Policy Breakdown", 14, yPos);
      yPos += 10;

      SECURITY_DATA.tables.forEach((table) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(12);
        doc.setTextColor(30, 64, 175);
        doc.text(`Table: ${table.table}`, 14, yPos);
        yPos += 5;

        const policyData = table.policies.map((p) => [p.name, p.command, p.permissive ? "Permissive" : "Restrictive"]);

        autoTable(doc, {
          startY: yPos,
          head: [["Policy Name", "Command", "Type"]],
          body: policyData,
          theme: "grid",
          headStyles: { fillColor: [100, 116, 139] },
          styles: { fontSize: 8 },
          margin: { left: 14 },
        });

        yPos = (doc as any).lastAutoTable.finalY + 10;
      });

      // Storage Buckets
      if (yPos > 230) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text("Storage Configuration", 14, yPos);
      yPos += 10;

      const bucketData = SECURITY_DATA.config.storageBuckets.map((b) => [b.name, b.isPublic ? "Public" : "Private"]);

      autoTable(doc, {
        startY: yPos,
        head: [["Bucket Name", "Access Level"]],
        body: bucketData,
        theme: "striped",
        headStyles: { fillColor: [30, 64, 175] },
        styles: { fontSize: 10 },
      });

      // Footer on each page
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`TED Monitor Security Report - Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
      }

      // Save
      doc.save(`ted-monitor-security-report-${new Date().toISOString().split("T")[0]}.pdf`);
      trackActivity('export_pdf', { type: 'security_report' });
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const totalTables = SECURITY_DATA.tables.length;
  const tablesWithRLS = SECURITY_DATA.tables.filter((t) => t.rlsEnabled).length;
  const totalPolicies = SECURITY_DATA.tables.reduce((acc, t) => acc + t.policies.length, 0);

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary" />
          {t('security.title')}
        </CardTitle>
        <CardDescription>{t('security.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-primary">{totalTables}</div>
            <div className="text-sm text-muted-foreground">{t('security.tables')}</div>
          </div>
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-green-600">{tablesWithRLS}</div>
            <div className="text-sm text-muted-foreground">{t('security.rlsEnabled')}</div>
          </div>
          <div className="text-center p-4 bg-muted rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{totalPolicies}</div>
            <div className="text-sm text-muted-foreground">{t('security.policies')}</div>
          </div>
        </div>

        {/* Security Status */}
        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle className="h-6 w-6 text-green-600" />
          <div>
            <div className="font-semibold text-green-800 dark:text-green-200">{t('security.allProtected')}</div>
            <div className="text-sm text-green-600 dark:text-green-400">{t('security.allProtectedDesc', { count: totalTables })}</div>
          </div>
        </div>

        {/* Configuration Status */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span>{t('security.anonSignupsDisabled')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span>{t('security.emailAutoConfirm')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span>{t('security.storageBucketPrivate')}</span>
          </div>
        </div>

        {/* Generate Button */}
        <Button onClick={generatePDF} disabled={isGenerating} className="w-full" size="lg">
          {isGenerating ? (
            <>{t('security.generating')}</>
          ) : (
            <>
              <Download className="mr-2 h-5 w-5" />
              {t('security.downloadPdf')}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
